import { useState, useCallback, useEffect, useMemo } from "react";
import Form, { Field as BaseField, useWatch } from "rc-field-form";
import { useTranslation } from "react-i18next";
import { omit } from "lodash";
import moment from "moment-timezone";
import Field from "../../../../../../components/Field";
import Text from "../../../../../../components/Inputs/Text";
import DescriptionField from "../../../../../../components/Field/DescriptionField";
import StatusField from "../../../../../../components/Field/StatusField";
import Checkbox from "../../../../../../components/Inputs/Checkbox";
import Button from "../../../../../../components/Button";
import Select from "../../../../../../components/Inputs/Select";
import ResourceSelect from "../../../../../../components/Inputs/ResourceSelect";
import DateInput from "../../../../../../components/Inputs/DateInput";
import Tier from "../../Form/Details/Tier";
import FixedOption from "../../Form/Details/FixedOption";
import HourlyOption from "../../Form/Details/HourlyOption";
import PerPayPeriod from "../../Form/Details/PerPayPeriod";
import { generateErrorsConfigForForm } from "../../../../../../utils/helpers/errors";
import {
    getSettingsResourceSearchFilters,
    renderSettingsResourceLabel,
} from "../../../../../../utils/helpers/settings";
import { useTimeOffVersionsApi } from "../api";

function Details({ entityId, mode, data: _data, submit, goBack, error, disabled }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [dateRange, setDateRange] = useState();
    const [fetch, setFetch] = useState(false);
    const startEffectiveDate = useWatch("startEffectiveDate", form);
    
    const { data: versions , getLatestVersion, loading } = useTimeOffVersionsApi();

    const data = useMemo(() => {
        if (mode === "edit") {
            return _data;
        } else {
            return versions.length !== 0 && { 
                ...versions?.[0], 
                ...versions?.[0]?.data
            };
        }
    }, [_data, mode, versions]);
    
    const [method, setMethod] = useState(data?.accural?.method || "hourly");

    const onStartDateChange = useCallback((value) => {
        setDateRange((prev) => ({
            ...prev,
            startDate: moment(value).format("YYYY-MM-DD")
        }));
    }, []);

    // const onEndDateChange = useCallback((value) => {
    //     setDateRange((prev) => ({
    //         ...prev,
    //         endDate: moment(value).format("YYYY-MM-DD")
    //     }));
    // }, []);

    const onFinish = useCallback(
        (values) => {
            const intervals = values.intervals.map((interval) => {
                const item = {};

                if (method === "fixed") {
                    item.value = Number(interval.value);
                } else if (method === "per-pay-period") {
                    item.value = Number(interval.value);
                    item.max = Number(interval.max);
                } else {
                    item.earned = Number(interval.earned);
                    item.worked = Number(interval.worked);
                    item.max = Number(interval.max);
                }

                return item;
            });

            const tiers = values.tiers.map((tier, i) => ({
                start: i === 0 ? null : Number(tier.start),
                end: tier?.end ? Number(tier.end) : null,
                name: tier.name,
            }));

            submit({
                ...omit(values, ["intervals", "earningGroups", "payCodes"]),
                tiers,
                accural: {
                    intervals,
                    method,
                    type: values?.accural?.type || undefined,
                    earningGroups: values?.accural?.earningGroups || undefined,
                    payCodes: values?.accural?.payCodes || undefined,
                },
            });
        },
        [submit, method]
    );

    const onTypeChange = useCallback(
        (value) => {
            if (value === "pay-codes") {
                form.setFieldValue(["accural", "earningGroups"], []);
            } else {
                form.setFieldValue(["accural", "payCodes"], []);
            }
        },
        [form]
    );

    const getValueFromEvent = useCallback((e) => e.target.value.toUpperCase(), []);

    useEffect(() => {
        form.resetFields();
        form.setFieldsValue({
            ...data,
            code: data?.code ? data.code : "",
            startEffectiveDate: data?.startEffectiveDate ? moment(dateRange?.startDate || data.startEffectiveDate).toDate() : null,
            endEffectiveDate: data?.endEffectiveDate ? moment(data.endEffectiveDate).toDate() : null,
            description: data?.description,
            status: data?.status ? data.status : "active",
            payCode: data?.payCode,
            intervals: data?.accural?.intervals || [],
            tierBy: data?.tierBy,
            tiers: data?.tiers || [{ start: null, end: null }],
            accural: {
                type: data?.accural?.type,
                payCodes: data?.accural?.payCodes || [],
                earningGroups: data?.accural?.earningGroups || [],
            },
        });
    }, [form, data, dateRange?.startDate]);

    useEffect(() => {
        const fieldErrors = generateErrorsConfigForForm(
            ["code", "description", "timeOffStatus", "payCode"],
            error
        );
        form.setFields(fieldErrors);
    }, [form, error]);

    useEffect(() => {
        const controller = new AbortController();
        if (mode === "create" && dateRange?.startDate) {
            const params = {
                startDate: dateRange.startDate,
                endDate: dateRange?.endDate ? dateRange.endDate : dateRange.startDate
            }
            getLatestVersion(entityId, params, controller);
        }
        return () => controller.abort();
    }, [dateRange, entityId, fetch, getLatestVersion, mode]);

    return (
        <Form form={form} onFinish={onFinish} className="d-flex flex-column justify-content-between h-100">
            <div>
                <Field
                    name="startEffectiveDate"
                    label={t("start-effective-date")}
                    rules={[
                        {
                            required: true,
                            message: t("required-start-effective-date"),
                        },
                        () => ({
                            validator(_, value) {
                                if (!value) return Promise.resolve();

                                const today = moment().startOf("day");
                                const selected = moment(value).startOf("day");

                                if (!selected.isAfter(today)) {
                                    return Promise.reject(new Error(t("start-date-must-be-in-the-future")));
                                }

                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <DateInput
                        minDate={moment().add(1, "day").toDate()}
                        placeholderText={t("start-effective-date")}
                        onChange={onStartDateChange}
                    />
                </Field>

                {/* <Field
                    name="endEffectiveDate"
                    label={t("end-effective-date")}
                    rules={[
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                const start = getFieldValue("startEffectiveDate");

                                if (!value || !start) return Promise.resolve();

                                const startDate = moment(start).startOf("day");
                                const endDate = moment(value).startOf("day");

                                if (!endDate.isAfter(startDate)) {
                                    return Promise.reject(new Error(t("end-date-must-be-after-start")));
                                }

                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <DateInput
                        minDate={moment().add(1, "day").toDate()}
                        placeholderText={t("end-effective-date")}
                        onChange={onEndDateChange}
                    />
                </Field> */}

                {startEffectiveDate !== null && (
                    <>
                        <Field
                            name="code"
                            label={t("name")}
                            getValueFromEvent={getValueFromEvent}
                            rules={[
                                {
                                    required: true,
                                    message: t("required-name"),
                                },
                                {
                                    message: t("name-length"),
                                    min: 1,
                                    max: 10,
                                },
                                {
                                    pattern: /^[a-zA-Z0-9]+$/,
                                    message: t("name-pattern"),
                                },
                            ]}
                        >
                            <Text placeholder={t("name")} disabled={true} />
                        </Field>

                        <DescriptionField />

                        <StatusField />

                        <Field
                            name="payCode"
                            className="mt-2"
                            label={t("pay-code")}
                            rules={[
                                {
                                    required: true,
                                    message: t("required-pay-codes"),
                                },
                            ]}
                        >
                            <ResourceSelect
                                hasSearch
                                placeholder={t("pay-codes")}
                                resourcePath="/pay-code"
                                renderLabel={renderSettingsResourceLabel}
                                getSearchFilters={getSettingsResourceSearchFilters}
                            />
                        </Field>

                        <Tier disabled={disabled} />

                        <h5>{t("accrual-method")}</h5>

                        <Checkbox
                            id="fixed"
                            onChange={() => setMethod("fixed")}
                            checked={method === "fixed"}
                            disabled={disabled}
                        >
                            {t("fixed-option")}
                        </Checkbox>

                        <Checkbox
                            id="hourly"
                            onChange={() => setMethod("hourly")}
                            checked={method === "hourly"}
                            disabled={disabled}
                        >
                            {t("hourly-option")}
                        </Checkbox>

                        <Checkbox
                            id="per-pay-period"
                            onChange={() => setMethod("per-pay-period")}
                            checked={method === "per-pay-period"}
                            disabled={disabled}
                        >
                            {t("per-pay-period")}
                        </Checkbox>

                        {method === "fixed" ? (
                            <FixedOption disabled={disabled} />
                        ) : method === "hourly" ? (
                            <HourlyOption form={form} disabled={disabled} />
                        ) : (
                            <PerPayPeriod disabled={disabled} />
                        )}

                        {["hourly", "per-pay-period"].includes(method) && (
                            <>
                                <Field
                                    name={["accural", "type"]}
                                    label={t("earning-type")}
                                    className="pt-2"
                                    rules={[
                                        {
                                            required: true,
                                            message: t("earning-type-required"),
                                        },
                                    ]}
                                >
                                    <Select placeholder={t("earning-type")} onChange={onTypeChange}>
                                        <Select.Option value="pay-codes">{t("pay-codes")}</Select.Option>
                                        <Select.Option value="earning-groups">{t("earning-groups")}</Select.Option>
                                    </Select>
                                </Field>

                                <BaseField>
                                    {({}, {}, { getFieldValue }) => {
                                        const type = getFieldValue(["accural", "type"]);

                                        return (
                                            <>
                                                {type === "pay-codes" && (
                                                    <Field
                                                        name={["accural", "payCodes"]}
                                                        className="mt-2"
                                                        label={t("eligible-pay-codes")}
                                                        placeholder={t("eligible-pay-codes")}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t("required-pay-codes"),
                                                            },
                                                        ]}
                                                    >
                                                        <ResourceSelect
                                                            hasSearch
                                                            resourcePath="/pay-code"
                                                            renderLabel={renderSettingsResourceLabel}
                                                            getSearchFilters={getSettingsResourceSearchFilters}
                                                            mode="multiple"
                                                            placeholder={t("eligible-pay-codes")}
                                                        />
                                                    </Field>
                                                )}

                                                {type === "earning-groups" && (
                                                    <Field
                                                        name={["accural", "earningGroups"]}
                                                        label={t("earning-groups")}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t("earning-group-required"),
                                                            },
                                                        ]}
                                                    >
                                                        <ResourceSelect
                                                            hasSearch
                                                            resourcePath="/earning-group"
                                                            mode="multiple"
                                                            renderLabel={renderSettingsResourceLabel}
                                                            getSearchFilters={getSettingsResourceSearchFilters}
                                                            placeholder={t("earning-groups")}
                                                        />
                                                    </Field>
                                                )}
                                            </>
                                        );
                                    }}
                                </BaseField>
                            </>
                        )}
                    </>
                )}
            </div>

            <div className="d-flex justify-content-end">
                <Button className="btn-round btn-icon shadow-none border" size="sm" onClick={goBack}>
                    {t("cancel")}
                </Button>

                <Button
                    type="submit"
                    className="btn-round btn-icon shadow-none border px-3"
                    color="dark"
                    size="sm"
                    disabled={startEffectiveDate === null}
                >
                    {t("continue")}
                </Button>
            </div>
        </Form>
    );
}

export default Details;
